/**
 * a2-pdp.js — controller for the A2 product template (templates/product.a2.json).
 * Spec: docs/a2/02-BEHAVIOUR.md.
 *
 * Reuse, not forking:
 *  - Mix-and-match DATA is the same as mix-match.js uses (custom.mix_match_group
 *    tops / bottoms / product_map, "Top__Bottom" keys), emitted by snippets/a2-data.liquid.
 *  - Product switching uses the Section Rendering API like mix-match does, against the
 *    a2 template (?view=a2 kept so it also works before products are assigned).
 *  - Add to cart is the THEME's own flow: the form's [data-add-to-cart] button is handled
 *    by theme.dev.js CartDrawer.cartAddEvent (add → refresh cart-drawer-v2 → open drawer).
 *    This file only manages labels/state around it.
 * Nothing from assets/mix-match.js is loaded or copied.
 */
(function () {
  'use strict';
  if (window.A2Pdp) return;

  var LS_USUAL = 'ys_usual_size';
  var DEFAULT_MAP = '2|Stretch I|169\n4|Stretch I|249\n6|Stretch I|287\n8|Stretch I|253\n10|Stretch I|179\n12|Stretch I|96\n14|Stretch II|15\n16|Stretch II|20\n18|Stretch II|26\n20|Stretch II|16\n22|Stretch II|7\n24|Stretch II|2';
  var mqDesktop = window.matchMedia('(min-width: 1024px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function readJSON(root, attr) {
    var el = root.querySelector('script[' + attr + ']');
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function track(name, data) {
    data = data || {};
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data)); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data); } catch (e) {}
  }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }
  /* ---------- Sizing (snippets/a2-size-system.liquid) ----------
     Size data stays keyed by US number; these show it in the shopper's system:
     US (USD store), UK (GBP store) or AU (GBP store, Australia / New Zealand). UK/AU = US + 4. */
  var SIZING = null;
  function sizing() {
    if (SIZING) return SIZING;
    var cfg = readJSON(document, 'data-a2-config') || readJSON(document, 'data-a2-hiw-config') || {};
    SIZING = cfg.sizing || { label: 'US', offset: 0, height: 'imperial' };
    return SIZING;
  }
  function sizeNum(us) { var n = parseInt(us, 10); return isNaN(n) ? us : n + (sizing().offset || 0); }
  function fmtSize(us) { return sizing().label + ' ' + sizeNum(us); }
  // JS mirror of snippets/a2-localise.liquid, for strings built in the browser.
  function localise(t) {
    var z = sizing(); if (!t || z.label === 'US') return t;
    return String(t)
      .replace(/US\s*(\d+)\s*([–-])\s*(\d+)\s*·\s*UK\s*(\d+)\s*[–-]\s*(\d+)/g, function (m, a, d, b, c, e) { return z.label + ' ' + c + d + e; })
      .replace(/\bUS\s*(\d+)\s*([–\-\/])\s*(\d+)/g, function (m, a, d, b) { return z.label + ' ' + sizeNum(a) + d + sizeNum(b); })
      .replace(/\bUS\s+(\d+)\b/g, function (m, a) { return z.label + ' ' + sizeNum(a); })
      .replace(/\bUS(?=\s+(\[size\]|sizes?\b))/g, z.label);
  }
  function lockScroll(on) { document.documentElement.classList.toggle('ys-scroll-lock', !!on); }
  function isSolo(name) { return / only$/i.test(name || ''); }
  function isOnePiece(name) { return /one[- ]?piece|dress|romper/i.test(name || ''); }
  function parseUsualMap(str) {
    var rows = String(str || '').trim() ? String(str) : DEFAULT_MAP;
    return rows.split(/\n/).map(function (r) {
      var c = r.split('|').map(function (x) { return x.trim(); });
      return c.length >= 2 ? { size: c[0], stretch: c[1], count: parseInt(c[2], 10) || 0 } : null;
    }).filter(Boolean);
  }

  /* Swipe for carousels: touch and mouse drag (pointer events) plus two-finger trackpad swipes
     (horizontal wheel). cb(+1) = next, cb(-1) = previous. Horizontal trackpad gestures are
     consumed so they don't trigger the browser's back/forward navigation. */
  function bindSwipe(el, cb) {
    var x0 = null, y0 = null, pid = null, acc = 0, lockUntil = 0;
    // Release is heard on the window, so a swipe that ends outside the carousel still counts.
    function up(e) {
      window.removeEventListener('pointerup', up);
      if (x0 == null || e.pointerId !== pid) return;
      var dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) { el._a2Swiped = Date.now(); cb(dx < 0 ? 1 : -1); }
    }
    el.addEventListener('pointerdown', function (e) {
      if (e.button > 0) return; x0 = e.clientX; y0 = e.clientY; pid = e.pointerId;
      window.addEventListener('pointerup', up);
    }, { passive: true });
    el.addEventListener('pointercancel', function () { x0 = null; });
    // Dragging an image (e.g. a press logo) would start the browser's image drag and cancel the swipe.
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    // A drag shouldn't also count as a click on whatever it started on.
    el.addEventListener('click', function (e) { if (el._a2Swiped && Date.now() - el._a2Swiped < 350) { e.preventDefault(); e.stopPropagation(); } }, true);
    el.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      if (Date.now() < lockUntil) return;
      acc += e.deltaX;
      if (Math.abs(acc) > 50) { cb(acc > 0 ? 1 : -1); acc = 0; lockUntil = Date.now() + 650; }
    }, { passive: false });
  }

  /* Shared usual-size state between the buy box and the How-it-works modal. */
  var Usual = {
    get: function () { return lsGet(LS_USUAL) || ''; },
    set: function (size, source) {
      lsSet(LS_USUAL, size);
      document.dispatchEvent(new CustomEvent('a2:usual-size', { detail: { size: size, source: source } }));
    }
  };

  /* ---------------------------------------------------------------------
     Main section controller. Bound to the #shopify-section-* wrapper so the
     delegated listeners survive a style switch (whose inner HTML is swapped).
     --------------------------------------------------------------------- */
  function Main(wrapper) {
    this.wrapper = wrapper;
    this.fetchCache = {};
    this.bindOnce();
    this.hydrate();
  }

  Main.prototype.hydrate = function () {
    var root = this.root = $('[data-a2-main]', this.wrapper);
    if (!root) return;
    this.sectionId = root.dataset.sectionId;
    this.config = readJSON(root, 'data-a2-config') || {};
    this.variants = readJSON(root, 'data-a2-variants') || {};
    this.media = readJSON(root, 'data-a2-media') || [];
    this.map = parseUsualMap(this.config.usualSizeMap);
    this.labels = this.config.labels || {};
    this.bundleCount = (this.config.bundle && this.config.bundle.count) || 0;
    this.variantList = Object.keys(this.variants).map(function (k) { return this.variants[k]; }, this);
    this.stretchValues = $$('[data-a2-stretch]', root).map(function (i) { return i.value; });

    var checkedC = $('[data-a2-colour]:checked', root);
    var checkedS = $('[data-a2-stretch]:checked', root);
    this.state = { colour: checkedC ? checkedC.value : '', stretch: checkedS ? checkedS.value : '' };

    this.initGalleryDots();
    this.initCarousel();
    this.initSticky();
    this.initFloating();
    this.initUsual();
    this.initDesc();
    this.initGift();
    this.updateVariantUI(false);
    var bs = this.bundleState();
    if (bs) {
      if (!$('[data-a2-bundle-live]', root)) { var live = document.createElement('span'); live.className = 'ys-sr'; live.setAttribute('aria-live', 'polite'); live.setAttribute('data-a2-bundle-live', ''); root.appendChild(live); }
      if (!window.__a2BundleViewed) { window.__a2BundleViewed = true; track('pdp_bundle_view', { state: bs.state, count: bs.n }); }
    }
  };

  Main.prototype.bindOnce = function () {
    var self = this, w = this.wrapper;
    w.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-a2-colour]')) { self.setState({ colour: t.value }, 'card'); track('pdp_colour_change', { colour: t.value }); }
      else if (t.matches('[data-a2-stretch]')) { self.setState({ stretch: t.value }, 'card'); self.onManualStretch(); track('pdp_stretch_change', { source: 'card', stretch: t.value }); }
      else if (t.matches('[data-a2-usual]')) { self.applyUsual(t.value, 'usual_size'); Usual.set(t.value, 'buybox'); track('pdp_usual_size_selected', { usual_size: t.value }); }
      else if (t.matches('[data-a2-gift-toggle]')) { self.toggleGift(t.checked); track('pdp_gift_toggle', { on: t.checked }); }
    });
    w.addEventListener('input', function (e) {
      var t = e.target;
      if (t.matches('[data-a2-gift-email]')) self.giftError('email', false);
      else if (t.matches('[data-a2-gift-date]')) self.giftError('date', false);
    });
    w.addEventListener('mouseover', function (e) {
      var lab = e.target.closest && e.target.closest('.a2-swatch');
      if (lab && mqDesktop.matches) { var n = $('[data-a2-colour-name]', self.root); if (n) n.textContent = $('input', lab).value; }
    });
    w.addEventListener('mouseout', function (e) {
      var lab = e.target.closest && e.target.closest('.a2-swatch');
      if (lab && mqDesktop.matches) { var n = $('[data-a2-colour-name]', self.root); if (n) n.textContent = self.state.colour; }
    });
    w.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target : e.target.parentElement;
      var open = t.closest('[data-a2-switch-open]');
      if (open) { self.toggleSwitcher(open.dataset.a2SwitchOpen, open); return; }
      if (t.closest('[data-a2-sw-close]')) { self.closeSwitcher(); return; }
      var tile = t.closest('[data-a2-tile]');
      if (tile && !tile.disabled) { self.chooseTile(tile); return; }
      var more = t.closest('[data-a2-desc-more]');
      if (more) { var dsc = more.closest('[data-a2-desc]'); if (dsc) dsc.classList.add('is-open'); more.hidden = true; return; }
      if (t.closest('[data-a2-rc-prev]')) { self.stepCarousel(-1); return; }
      if (t.closest('[data-a2-rc-next]')) { self.stepCarousel(1); return; }
      if (t.closest('[data-a2-fatc-btn]')) {
        var atc = $('[data-a2-atc]', self.root);
        if (atc && !atc.disabled) { self.atcSource = 'floating'; atc.click(); }
        return;
      }
      if (t.closest('[data-a2-atc]')) {
        // Gift card recipient: the theme posts the form without the browser's checks, so stop here
        // (before its document listener) while the email or date isn't right.
        if (!self.giftValid()) { e.preventDefault(); e.stopPropagation(); self.atcSource = null; return; }
        self.atcSource = self.atcSource || 'main'; self.onAtcStart();
      }
      var dlg = $('[data-a2-sw]', self.root);
      if (dlg && dlg.open && e.target === dlg) self.closeSwitcher(); // backdrop tap (M)
    }, true);
    w.addEventListener('mouseenter', function (e) {
      var tile = e.target.closest && e.target.closest('[data-a2-tile]');
      if (tile && !tile.disabled) self.prefetch(tile.dataset.a2Handle);
    }, true);
    w.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { var d = $('[data-a2-sw]', self.root); if (d && d.open) { e.preventDefault(); self.closeSwitcher(); } }
      var tile = e.target.closest && e.target.closest('[data-a2-tile]');
      if (tile && /^Arrow/.test(e.key)) self.moveTileFocus(tile, e.key, e);
    });
    w.addEventListener('toggle', function (e) {
      if (e.target.matches && e.target.matches('[data-a2-acc]')) self.recalcSticky();
    }, true);
    document.addEventListener('click', function (e) {
      var d = self.root && $('[data-a2-sw]', self.root);
      if (d && d.open && mqDesktop.matches && !e.target.closest('[data-a2-switch]')) self.closeSwitcher();
    });
    document.addEventListener('a2:usual-size', function (e) {
      if (e.detail.source === 'buybox') return;
      var sel = self.root && $('[data-a2-usual]', self.root);
      if (sel) sel.value = e.detail.size;
      self.applyUsual(e.detail.size, 'modal');
    });
    document.addEventListener('a2:choose-stretch', function (e) {
      self.setState({ stretch: e.detail.stretch }, 'modal');
      track('pdp_stretch_change', { source: 'modal', stretch: e.detail.stretch });
      var atc = $('[data-a2-atc]', self.root);
      if (atc) { atc.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' }); setTimeout(function () { atc.focus({ preventScroll: true }); }, 400); }
    });
    document.addEventListener('theme:cart:added', function () { self.onAtcDone(true); self.refreshBundle(200); self.resetGift(); });
    // Bundle offer: re-count after cart changes. The drawer fires theme:cart:update before its own
    // request finishes, so look twice; and again when the drawer closes or the page comes back.
    document.addEventListener('theme:cart:update', function () { self.refreshBundle(600); self.refreshBundle(1600); });
    document.addEventListener('theme:cart:close', function () { self.refreshBundle(100); });
    var fitT; window.addEventListener('resize', function () { clearTimeout(fitT); fitT = setTimeout(function () { self.fitPill(); }, 150); });
    window.addEventListener('pageshow', function (e) { if (e.persisted) self.refreshBundle(0); });
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') self.refreshBundle(0); });
    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.a2 && e.state.handle && e.state.handle !== (self.config && self.config.currentHandle)) {
        self.switchProduct(e.state.handle, null, false);
      }
    });
  };

  /* ---------- Variant pipeline (§5) ---------- */
  Main.prototype.findVariant = function (colour, stretch) {
    for (var i = 0; i < this.variantList.length; i++) {
      var v = this.variantList[i];
      if (v.colour === colour && v.stretch === stretch) return v;
    }
    return null;
  };
  Main.prototype.otherStretch = function (s) {
    for (var i = 0; i < this.stretchValues.length; i++) if (this.stretchValues[i] !== s) return this.stretchValues[i];
    return '';
  };
  Main.prototype.setState = function (patch, source) {
    var prev = Object.assign({}, this.state);
    Object.assign(this.state, patch);
    this.updateVariantUI(true, prev);
  };
  Main.prototype.currentVariant = function () { return this.findVariant(this.state.colour, this.state.stretch); };

  Main.prototype.updateVariantUI = function (changed, prev) {
    var root = this.root, st = this.state, L = this.labels, self = this;
    var v = this.currentVariant();
    // Colour swatches show sold out for the selected Stretch, so changing Stretch updates them.
    $$('[data-a2-colour]', root).forEach(function (i) {
      i.checked = i.value === st.colour;
      var cv = self.findVariant(i.value, st.stretch), out = !cv || !cv.available;
      var sw = i.closest('.a2-swatch'); if (sw) sw.classList.toggle('is-unavailable', out);
      var sr = sw && $('[data-a2-swatch-sr]', sw); if (sr) sr.textContent = i.value + (out ? ' — ' + L.soldOut : '');
    });
    $$('[data-a2-stretch]', root).forEach(function (i) {
      i.checked = i.value === st.stretch;
      var sv = self.findVariant(st.colour, i.value);
      // Stretch tile (swimwear) or simple option button (accessory layout).
      var card = i.closest('.a2-stretch__card, .a2-opt__pill');
      var unavailable = !sv || !sv.available;
      if (card) card.classList.toggle('is-soldout', unavailable);
      var r = card && $('[data-a2-range]', card);
      if (r) r.textContent = r.dataset.a2Range + (unavailable ? ' · ' + L.soldOut : '');
    });
    var name = $('[data-a2-colour-name]', root); if (name) name.textContent = st.colour;
    var optName = $('[data-a2-opt-name]', root); if (optName) optName.textContent = st.stretch;

    var input = $('[data-a2-variant-input]', root);
    var atc = $('[data-a2-atc]', root), lbl = $('[data-a2-atc-label]', root);
    var avail = !!(v && v.available);
    if (input && v) input.value = v.id;
    if (atc) { atc.disabled = !avail; if (!this.adding) lbl.textContent = avail ? L.atc + ' — ' + v.price : L.soldOut; }
    var price = $('[data-a2-price]', root), priceM = $('[data-a2-price-m]', root);
    if (v) {
      // Sale badge, or the bundle pill while a multi-buy offer applies (badgeHTML).
      var bs = this.bundleState();
      var anim = !!(bs && this.pillState && this.pillState !== bs.state);
      this.pillState = bs ? bs.state : null;
      var base = v.price + (v.compare ? ' <s class="a2-compare">' + v.compare + '</s>' : '');
      if (price) { price.innerHTML = base + this.badgeHTML(v, false, bs, anim); this.fitPill(); }
      // Mobile: in the rating row when there is one (short labels), otherwise under the prices.
      var saleM = $('[data-a2-sale-m]', root), badgeM = this.badgeHTML(v, true, bs, anim);
      if (saleM) saleM.innerHTML = badgeM;
      if (priceM) priceM.innerHTML = saleM ? base : base + badgeM;
    }
    this.updateFloating(v);
    if (!changed) return;

    if (v) history.replaceState(history.state, '', this.urlFor(this.config.currentHandle, v.id));
    if (prev && (prev.colour !== st.colour || prev.stretch !== st.stretch)) this.renderGallery(prev.colour !== st.colour);
    if (v) root.dispatchEvent(new CustomEvent('theme:variant:change', { bubbles: true, detail: { variant: v } }));
  };

  Main.prototype.urlFor = function (handle, variantId) {
    var view = new URLSearchParams(location.search).get('view');
    var q = new URLSearchParams();
    if (variantId) q.set('variant', variantId);
    if (view) q.set('view', view);
    var pv = new URLSearchParams(location.search).get('preview_theme_id');
    if (pv) q.set('preview_theme_id', pv);
    return '/products/' + handle + (q.toString() ? '?' + q.toString() : '');
  };

  /* ---------- ATC state around the theme's own flow ---------- */
  Main.prototype.onAtcStart = function () {
    var atc = $('[data-a2-atc]', this.root);
    if (!atc || atc.disabled) return;
    this.adding = true;
    $('[data-a2-atc-label]', this.root).textContent = this.labels.adding;
    var f = $('[data-a2-fatc-btn]', this.root); if (f) f.textContent = this.labels.adding;
    var err = $('[data-a2-atc-error]', this.root); if (err) err.hidden = true;
    track('pdp_atc', { source: this.atcSource || 'main' });
    var self = this;
    clearTimeout(this.atcTimer);
    this.atcTimer = setTimeout(function () { self.onAtcDone(false); }, 8000);
  };
  Main.prototype.onAtcDone = function (ok) {
    if (!this.adding) return;
    clearTimeout(this.atcTimer);
    var self = this, atc = $('[data-a2-atc]', this.root);
    if (ok) {
      $('[data-a2-atc-label]', this.root).textContent = this.labels.added;
      var f = $('[data-a2-fatc-btn]', this.root); if (f) f.textContent = this.labels.added;
    }
    setTimeout(function () {
      self.adding = false; self.atcSource = null;
      if (atc) atc.classList.remove('is-loading');
      self.updateVariantUI(false);
    }, ok ? 1200 : 0);
  };

  /* ---------- Gift card: send to a recipient (snippets/a2-gift-recipient.liquid) ---------- */
  function isoDay(d) { return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2); }
  Main.prototype.initGift = function () {
    var g = $('[data-a2-gift]', this.root);
    if (!g) return;
    // Shopify sends on a date from today up to 90 days ahead, in the shopper's time zone.
    var now = new Date(), max = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 90);
    var d = $('[data-a2-gift-date]', g); if (d) { d.min = isoDay(now); d.max = isoDay(max); }
    var off = $('[data-a2-gift-offset]', g); if (off) off.value = now.getTimezoneOffset();
    var tg = $('[data-a2-gift-toggle]', g); this.toggleGift(!!(tg && tg.checked));
  };
  Main.prototype.toggleGift = function (on) {
    var g = $('[data-a2-gift]', this.root);
    if (!g) return;
    var fields = $('[data-a2-gift-fields]', g), tg = $('[data-a2-gift-toggle]', g);
    // Disabled fields aren't posted, so an unticked box adds nothing to the order.
    $$('input, textarea', fields).forEach(function (f) { f.disabled = !on; });
    fields.hidden = !on;
    if (tg) tg.setAttribute('aria-expanded', on ? 'true' : 'false');
    g.classList.toggle('is-open', on);
    if (!on) { this.giftError('email', false); this.giftError('date', false); }
    this.recalcSticky();
  };
  Main.prototype.giftError = function (key, show) {
    var g = $('[data-a2-gift]', this.root);
    var err = g && $('[data-a2-gift-err="' + key + '"]', g);
    var input = g && $(key === 'email' ? '[data-a2-gift-email]' : '[data-a2-gift-date]', g);
    if (err) err.hidden = !show;
    if (input) { if (show) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
  };
  Main.prototype.giftValid = function () {
    var g = $('[data-a2-gift]', this.root), tg = g && $('[data-a2-gift-toggle]', g);
    if (!tg || !tg.checked) return true;
    var email = $('[data-a2-gift-email]', g), date = $('[data-a2-gift-date]', g), first = null;
    var ev = (email.value || '').trim(); email.value = ev;
    var okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ev);
    this.giftError('email', !okEmail); if (!okEmail) first = email;
    var dv = date ? date.value : '';
    var okDate = !dv || (/^\d{4}-\d{2}-\d{2}$/.test(dv) && dv >= date.min && dv <= date.max);
    this.giftError('date', !okDate); if (!okDate && !first) first = date;
    if (first) {
      first.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      setTimeout(function () { first.focus({ preventScroll: true }); }, reduceMotion.matches ? 0 : 350);
      track('pdp_gift_invalid', { field: first === email ? 'email' : 'date' });
      return false;
    }
    return true;
  };
  Main.prototype.resetGift = function () {
    var g = $('[data-a2-gift]', this.root), tg = g && $('[data-a2-gift-toggle]', g);
    if (!tg || !tg.checked) return;
    // Like the theme's own recipient form: the next gift starts empty.
    $$('[data-a2-gift-fields] input:not([type="hidden"]), [data-a2-gift-fields] textarea', g).forEach(function (f) { f.value = ''; });
    tg.checked = false; this.toggleGift(false);
  };

  /* ---------- Gallery (§3) ---------- */
  Main.prototype.galleryCells = function () {
    var st = this.state, c = (st.colour || '').toLowerCase(), s = (st.stretch || '').toLowerCase();
    var o = this.otherStretch(st.stretch).toLowerCase();
    var sel = [], oth = [], un = [], all = [];
    this.media.forEach(function (m) {
      all.push(m);
      if (m.colour !== c) return;
      // o is '' on a product made in one Stretch: never match it, or every untagged photo lands in
      // "other Stretch" (Io Romper showed its first photo twice, then another colour).
      if (m.stretch === s) sel.push(m); else if (o && m.stretch === o) oth.push(m); else if (!m.stretch) un.push(m);
    });
    var byId = {}; this.media.forEach(function (m) { byId[m.id] = m; });
    var vSel = this.findVariant(st.colour, st.stretch), vOth = this.findVariant(st.colour, this.otherStretch(st.stretch));
    var cells = [];
    // The Stretch pair (cells 1 and 2) is always a photo; product videos only ever fill an extra slot.
    var photo = function (list) { return list.filter(function (m) { return !m.video; }); };
    var selP = photo(sel), othP = photo(oth);
    var c1 = selP[0] || (vSel && byId[vSel.featuredMediaId]) || photo(un)[0] || photo(all)[0] || all[0];
    // tagged = we know the photo shows this Stretch: its alt text says so (sel / oth), or it is the
    // variant's own featured photo and NOT the same photo as the other Stretch's (a Stretch II variant
    // often reuses the Stretch I photo; then it's not a Stretch II shot). Alt text is optional.
    var fSel = vSel && vSel.featuredMediaId, fOth = vOth && vOth.featuredMediaId;
    var t1 = (!!selP[0] && c1 === selP[0]) || (!!fSel && c1 === byId[fSel] && fSel !== fOth);
    cells.push({ m: c1, variant: vSel, chip: true, tagged: t1 });
    var c2 = othP[0], chip2 = true;
    if (!c2 && vOth && byId[vOth.featuredMediaId] && byId[vOth.featuredMediaId] !== c1) c2 = byId[vOth.featuredMediaId];
    if (!c2) chip2 = false;
    var pool = sel.concat(un); if (pool.length < 2) pool = all;
    var gv = this.config.galleryVideo || 'last';
    var extras = pool.filter(function (m) { return m && m !== c1 && m !== c2 && !(gv === 'hide' && m.video); });
    if (!c2) { c2 = photo(extras)[0]; if (c2) extras.splice(extras.indexOf(c2), 1); }
    var t2 = (!!othP[0] && c2 === othP[0]) || (!!fOth && c2 === byId[fOth] && fOth !== fSel);
    if (c2) cells.push({ m: c2, variant: chip2 ? vOth : null, chip: chip2, tagged: t2 });
    // Product videos (galleryVideo): last = always shown, in the last extra slot; first = always shown,
    // first extra; order = media order, only if within the count; hide = never (filtered above).
    var vid = extras.filter(function (m) { return m.video; })[0];
    // Colour videos (A2 · Main blocks) listing this colour: each takes an extra photo's slot (even desktop
    // grid); one set to skip is left out when the product shows its own video here.
    var cn = c.trim(), cvs = (this.config.colourVideos || []).filter(function (cv) {
      if (cv.skip && vid) return false;
      return cn && String(cv.colours || '').toLowerCase().split(/[,\n]/).map(function (x) { return x.trim(); }).indexOf(cn) > -1;
    });
    var n = Math.max(0, (this.config.galleryExtra || 3) - cvs.length), shown;
    if (vid && gv !== 'order') {
      var rest = extras.filter(function (m) { return m !== vid; }).slice(0, n - 1);
      shown = gv === 'first' ? [vid].concat(rest) : rest.concat(vid);
    } else shown = extras.slice(0, n);
    shown.forEach(function (m) { cells.push({ m: m }); });
    // …after this colour's photos and videos, before the Versoform loop.
    cvs.forEach(function (cv) { cells.push({ m: { id: 'cv-' + cv.id, video: cv.html } }); });
    return cells.filter(function (x) { return x.m; });
  };
  Main.prototype.chipText = function (variant, stretch, mobile, tagged) {
    if (!variant || !this.config.chip || !this.config.chip.show) return '';
    var m = variant.model || {}, ys = m.youswim_size || stretch, us = m.normal_us;
    // Only name a Stretch we know: the photo's alt text says it (tagged) or the model record does.
    if (!tagged && !m.youswim_size) return '';
    var c = this.config.chip;
    // A per-Stretch photo with no model info: "Wears Stretch II" (blank setting = no chip).
    if (!us && !m.youswim_size) return c.fallback && stretch ? fill(c.fallback, { stretch: stretch }) : '';
    var on = mobile
      ? { name: c.mName, cup: c.mCup, height: c.mHeight, lead: '' }
      : { name: c.name, cup: true, height: c.height, lead: 'Wears ' };
    var out = (on.name && m.name ? String(m.name).trim() + ' wears ' : on.lead) + ys;
    // UK / AU: the model's own UK size when set, else US + 4; AU heights in cm.
    if (us) out += ' · usually ' + (sizing().label !== 'US' && m.normal_uk ? sizing().label + ' ' + m.normal_uk : fmtSize(us));
    if (on.cup && m.cup_size) out += ' · ' + m.cup_size + ' cup';
    var h = sizing().height === 'metric' && m.height_metric ? m.height_metric : m.height;
    if (on.height && h) out += ' · ' + h;
    return out;
  };
  Main.prototype.renderGallery = function (colourChanged) {
    var self = this, track_ = $('[data-a2-track]', this.root), gallery = $('[data-a2-gallery]', this.root);
    if (!track_) return;
    var loop = $('[data-a2-loop]', track_);
    var st = this.state, other = this.otherStretch(st.stretch);
    var cells = this.galleryCells();
    var title = this.config.title || '';
    var build = function () {
      $$('[data-a2-cell]:not([data-a2-loop])', track_).forEach(function (n) { n.remove(); });
      var frag = document.createDocumentFragment();
      cells.forEach(function (cell, i) {
        var div = document.createElement('div');
        div.className = 'a2-cell'; div.setAttribute('data-a2-cell', ''); div.dataset.mediaId = cell.m.id;
        if (cell.m.video) {
          // Product video: same lazy, muted, looping player as the server render (a2-video).
          div.innerHTML = cell.m.video;
        } else {
          var img = new Image();
          img.className = 'a2-cell__img';
          img.src = cell.m.src; img.srcset = cell.m.srcset;
          img.sizes = '(min-width: 1024px) 34vw, calc(100vw - 60px)';
          img.alt = cell.m.alt || (title + ' in ' + st.colour);
          img.loading = i < 2 ? 'eager' : 'lazy';
          div.appendChild(img);
        }
        if (cell.chip && cell.variant) {
          var s = i === 0 ? st.stretch : other;
          var d = self.chipText(cell.variant, s, false, cell.tagged), mo = self.chipText(cell.variant, s, true, cell.tagged);
          if (d) {
            var a = document.createElement('span'); a.className = 'ys-img-chip ys-only-d'; a.textContent = d; div.appendChild(a);
            var b = document.createElement('span'); b.className = 'ys-img-chip ys-only-m'; b.textContent = mo; div.appendChild(b);
          }
        }
        frag.appendChild(div);
      });
      track_.insertBefore(frag, loop || null);
      track_.scrollLeft = 0;
      self.initGalleryDots();
      gallery.classList.remove('is-fading');
    };
    // Preload the two front images, then cross-fade (150ms).
    var pre = cells.slice(0, 2).map(function (c) { return new Promise(function (res) { var i = new Image(); i.onload = i.onerror = res; i.srcset = c.m.srcset; i.sizes = '(min-width: 1024px) 34vw, 85vw'; i.src = c.m.src; }); });
    gallery.classList.add('is-fading');
    Promise.race([Promise.all(pre), new Promise(function (r) { setTimeout(r, 600); })]).then(function () { setTimeout(build, reduceMotion.matches ? 0 : 150); });
    if (colourChanged && mqDesktop.matches) {
      var r = gallery.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) gallery.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  };
  Main.prototype.initGalleryDots = function () {
    var track_ = $('[data-a2-track]', this.root), dots = $('[data-a2-dots]', this.root);
    if (!track_ || !dots) return;
    var cells = $$('[data-a2-cell]', track_);
    dots.innerHTML = cells.map(function (_, i) { return '<span class="a2-dot' + (i === 0 ? ' is-active' : '') + '"></span>'; }).join('');
    if (track_._a2Scroll) track_.removeEventListener('scroll', track_._a2Scroll);
    track_._a2Scroll = function () {
      var w = cells[0] ? cells[0].getBoundingClientRect().width + 4 : 1;
      var idx = Math.round(track_.scrollLeft / w);
      $$('.a2-dot', dots).forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    };
    track_.addEventListener('scroll', track_._a2Scroll, { passive: true });
    var loopVid = $('[data-a2-loop] video', track_);
    if (loopVid) {
      // Reduced motion: no autoplay; a play button starts it on request.
      if (reduceMotion.matches) {
        loopVid.removeAttribute('data-a2-autoplay');
        var p = $('[data-a2-play]', track_);
        if (p) { p.hidden = false; p.onclick = function () { loadVideo(loopVid); loopVid.play().catch(function () {}); p.hidden = true; }; }
      }
    }
    initLazyVideos(track_);
  };

  /* ---------- Usual size matcher (§2) ---------- */
  Main.prototype.initUsual = function () {
    var sel = $('[data-a2-usual]', this.root);
    var saved = Usual.get();
    if (!sel || !saved) return;
    sel.value = saved;
    var explicit = new URLSearchParams(location.search).get('variant');
    var row = this.rowFor(saved);
    if (explicit && row && row.stretch !== this.state.stretch) { this.showUsualResult(null); return; }
    this.applyUsual(saved, 'restore');
  };
  Main.prototype.rowFor = function (size) {
    for (var i = 0; i < this.map.length; i++) if (this.map[i].size === String(size)) return this.map[i];
    return null;
  };
  Main.prototype.applyUsual = function (size, source) {
    var row = this.rowFor(size);
    if (!row) { this.showUsualResult(null); return; }
    if (row.stretch !== this.state.stretch && this.stretchValues.indexOf(row.stretch) > -1) {
      this.setState({ stretch: row.stretch }, source);
      if (source !== 'restore') track('pdp_stretch_change', { source: 'usual_size', stretch: row.stretch });
    }
    this.showUsualResult(row);
  };
  Main.prototype.showUsualResult = function (row) {
    var res = $('[data-a2-usual-result]', this.root), line = $('[data-a2-usual-line]', this.root), bnd = $('[data-a2-boundary]', this.root);
    if (res) { res.hidden = !row; if (row) res.innerHTML = '→ <strong></strong>', res.querySelector('strong').textContent = row.stretch; }
    if (line) {
      var show = row && row.count >= (this.config.usualSizeMinCount || 10);
      line.hidden = !show;
      if (show) line.textContent = fill(this.config.usualSizeLine, { count: row.count, size: sizeNum(row.size), stretch: row.stretch });
    }
    if (bnd) {
      var b = row && this.config.showBoundaryInBuybox && (row.size === '12' || row.size === '14');
      bnd.hidden = !b; if (b) bnd.textContent = this.config.boundaryNote;
    }
  };
  Main.prototype.onManualStretch = function () {
    var sel = $('[data-a2-usual]', this.root);
    var row = sel && this.rowFor(sel.value);
    if (row && row.stretch !== this.state.stretch) this.showUsualResult(null);
    else if (row) this.showUsualResult(row);
  };

  /* ---------- Buy-box review carousel (§7) ---------- */
  Main.prototype.initCarousel = function () {
    var rc = $('[data-a2-rc]', this.root);
    this.rcIndex = 0;
    if (!rc) return;
    if (rc.hasAttribute('data-a2-rc-shuffle')) this.shuffleCarousel(rc);
    var self = this;
    bindSwipe(rc, function (dir) { self.stepCarousel(dir); });
  };
  // Random order (setting carousel_shuffle): Fisher–Yates over the slides, then make sure the
  // first one isn't the review this visitor opened on last time.
  Main.prototype.shuffleCarousel = function (rc) {
    var slides = $$('[data-a2-rc-slide]', rc);
    if (slides.length < 2) return;
    var holder = slides[0].parentNode, key = 'ys_rc_last', last = lsGet(key);
    for (var i = slides.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var t = slides[i]; slides[i] = slides[j]; slides[j] = t; }
    var sig = function (el) { return (el.querySelector('.a2-rc__quote') || el).textContent.trim().slice(0, 60); };
    if (last && sig(slides[0]) === last) slides.push(slides.shift());
    slides.forEach(function (el, i) { holder.appendChild(el); el.hidden = i !== 0; el.classList.toggle('is-active', i === 0); });
    lsSet(key, sig(slides[0]));
  };
  Main.prototype.stepCarousel = function (dir) {
    var slides = $$('[data-a2-rc-slide]', this.root);
    if (slides.length < 2) return;
    slides[this.rcIndex].hidden = true; slides[this.rcIndex].classList.remove('is-active');
    this.rcIndex = (this.rcIndex + dir + slides.length) % slides.length;
    slides[this.rcIndex].hidden = false; slides[this.rcIndex].classList.add('is-active');
    track('pdp_review_carousel', { direction: dir > 0 ? 'next' : 'prev' });
    this.recalcSticky();
  };

  /* ---------- Sticky buy box (§8) ---------- */
  Main.prototype.initSticky = function () {
    var self = this, aside = $('[data-a2-buybox]', this.root);
    if (!aside) return;
    if (this.ro) this.ro.disconnect();
    this.ro = new ResizeObserver(function () { self.recalcSticky(); });
    this.ro.observe(aside);
    if (!this._stickyBound) { window.addEventListener('resize', function () { self.recalcSticky(); }); this._stickyBound = true; }
    this.recalcSticky();
  };
  Main.prototype.recalcSticky = function () {
    var aside = this.root && $('[data-a2-buybox]', this.root), gallery = this.root && $('[data-a2-gallery]', this.root);
    if (!aside) return;
    if (!mqDesktop.matches || (gallery && gallery.offsetHeight <= aside.offsetHeight)) { aside.style.position = mqDesktop.matches ? 'relative' : ''; aside.style.top = ''; return; }
    aside.style.position = '';
    aside.style.top = Math.min(24, innerHeight - aside.offsetHeight - 24) + 'px';
  };

  /* ---------- Floating ATC (§6) ---------- */
  // Accessory description: show "Read more" only when the clamped text actually overflows.
  Main.prototype.initDesc = function () {
    var d = $('[data-a2-desc]', this.root); if (!d) return;
    var body = $('[data-a2-desc-body]', d), btn = $('[data-a2-desc-more]', d);
    var check = function () { if (!d.classList.contains('is-open')) btn.hidden = body.scrollHeight <= body.clientHeight + 2; };
    check(); window.addEventListener('load', check, { once: true });
  };
  Main.prototype.initFloating = function () {
    var self = this, bar = $('[data-a2-fatc]', this.root), atc = $('[data-a2-atc]', this.root);
    if (this.io) this.io.disconnect();
    if (!bar || !atc) return;
    this.atcVisible = true;
    this.io = new IntersectionObserver(function (es) { self.atcVisible = es[0].isIntersecting; self.syncFloating(); }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    this.io.observe(atc);
    if (!this._fatcBound) {
      window.addEventListener('scroll', function () { self.syncFloating(); }, { passive: true });
      new MutationObserver(function () { self.syncFloating(); }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this._fatcBound = true;
    }
    this.syncFloating();
  };
  Main.prototype.syncFloating = function () {
    var bar = this.root && $('[data-a2-fatc]', this.root);
    if (!bar) return;
    var trigger = (this.config.floatingTrigger || 25) / 100;
    // Hide when the page footer's top edge is within 80px of the viewport bottom (02-BEHAVIOUR §6).
    // This theme's footer is a fixed "parallax reveal" layer, so its own rect is meaningless;
    // the footer's top edge is where the main content ends.
    var main = document.querySelector('main') || document.getElementById('MainContent');
    var nearFooter = main ? main.getBoundingClientRect().bottom < innerHeight + 80 : false;
    var sheetOpen = !!document.querySelector('dialog.ys-dialog[open]') || document.body.classList.contains('js-drawer-open-cart') || document.body.classList.contains('js-drawer-open');
    var enabled = mqDesktop.matches ? this.config.floatingAtcDesktop === true : this.config.floatingAtc !== false;
    // 'past' (default): only once the main Add to cart has scrolled up off the screen, so never at the
    // top of the page while it is still below the fold. 'out': whenever it is out of view (desktop
    // from load; mobile after scrolling the set %).
    var shouldShow;
    if (this.config.floatingWhen === 'out') {
      var scrolled = mqDesktop.matches || window.scrollY > innerHeight * trigger;
      shouldShow = scrolled && !this.atcVisible;
    } else {
      var atc = $('[data-a2-atc]', this.root);
      shouldShow = !!atc && atc.getBoundingClientRect().bottom < 0;
    }
    var show = enabled && shouldShow && !sheetOpen && !nearFooter;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (show) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
    var strip = !mqDesktop.matches && $('[data-a2-fatc-strip]', this.root);
    var stripOn = show && !!strip && !strip.hidden;
    document.body.classList.toggle('a2-fatc-on', show);
    document.body.classList.toggle('a2-fatc-strip', stripOn);
    document.documentElement.style.setProperty('--ys-fatc-offset', show ? (stripOn ? '102px' : '76px') : '0px');
  };
  /* ---------- Bundle offer (bundle-pill handoff) ---------- */
  // null when the offer doesn't apply (product / country decided in Liquid; dates re-checked here so
  // a cached page stops showing it at the end date), else the state for the eligible cart count.
  Main.prototype.bundleState = function () {
    var b = this.config.bundle;
    if (!b || !b.on) return null;
    var now = Date.now();
    if (b.starts && now < Date.parse(b.starts)) return null;
    if (b.ends && now > Date.parse(b.ends)) return null;
    var q = b.qty2 || 2, n = this.bundleCount || 0;
    return { b: b, n: n, q: q, rem: Math.max(q - n, 0), fill: Math.min(100, Math.round(n * 100 / q)), state: n >= q ? 'unlocked' : n > 0 ? 'progress' : 'empty' };
  };
  Main.prototype.bundleMap = function (v, bs) {
    var pct = v && v.compare && v.pct > 0 ? v.pct : 0;
    return { percent: bs.b.tier1 > 0 ? bs.b.tier1 : pct, percent2: bs.b.tier2, qty2: bs.q, remaining: bs.rem };
  };
  Main.prototype.bundleSentence = function (v, bs) {
    var m = this.bundleMap(v, bs);
    if (bs.state === 'unlocked') return 'Bundle offer: ' + m.percent2 + '% off unlocked.';
    return 'Bundle offer: ' + (m.percent > 0 ? m.percent + '% off one item, ' : '') + m.percent2 + '% off ' + m.qty2 + ' or more.' +
      (bs.n > 0 ? ' Add ' + m.remaining + ' more for ' + m.percent2 + '% off.' : '');
  };
  // Pill HTML (same markup as snippets/a2-bundle-pill.liquid), or today's sale badge.
  Main.prototype.badgeHTML = function (v, mobile, bs, anim) {
    if (!v) return '';
    var pct = v.compare && v.pct > 0 ? v.pct : 0;
    if (!bs) return pct && this.labels.saleBadge ? ' <span class="a2-sale-badge">' + esc(fill(this.labels.saleBadge, { percent: pct })) + '</span>' : '';
    var L = bs.b.labels || {}, m = this.bundleMap(v, bs);
    var cls = 'a2-bpill is-' + bs.state + (anim ? ' a2-bpill--in' : '');
    var inner;
    if (bs.state === 'unlocked') {
      inner = '<span class="a2-bpill__u" aria-hidden="true"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>' + esc(fill(L.unlocked, m)) + '</span>';
    } else {
      var t1 = mobile && L.tier1M ? L.tier1M : L.tier1, pr = mobile && L.progressM ? L.progressM : L.progress;
      inner = (m.percent > 0 ? '<span class="a2-bpill__a" aria-hidden="true">' + esc(fill(t1, m)) + '</span>' : '') +
        (bs.state === 'progress'
          ? '<span class="a2-bpill__b" aria-hidden="true" style="--a2-bp: ' + bs.fill + '%">' + esc(fill(pr, m)) + '</span>'
          : '<span class="a2-bpill__b" aria-hidden="true">' + esc(fill(L.tier2, m)) + '</span>');
    }
    return ' <span class="' + cls + '" role="status" aria-label="' + esc(this.bundleSentence(v, bs)) + '" data-a2-bpill="' + bs.state + '">' + inner + '</span>';
  };
  // Desktop price row: the pill sits beside the price with the badge's 8px gap; when it doesn't fit it
  // drops onto its own line, flush left under the price (no stray 8px indent).
  Main.prototype.fitPill = function () {
    var price = this.root && $('[data-a2-price]', this.root), pill = price && $('.a2-bpill', price);
    if (!pill || !price.offsetParent) return;
    pill.classList.remove('is-wrapped');
    var first = price.firstElementChild && price.firstElementChild !== pill ? price.firstElementChild : price;
    if (pill.getBoundingClientRect().top >= first.getBoundingClientRect().bottom - 4) pill.classList.add('is-wrapped');
  };
  // Floating bar offer: an extra line (inline) or a band on top of the bar (strip, mobile).
  Main.prototype.updateBundleFloating = function (v) {
    var root = this.root, bar = root && $('[data-a2-fatc]', root);
    if (!bar) return;
    var line = $('[data-a2-fatc-offer]', root), strip = $('[data-a2-fatc-strip]', root);
    if (!line && !strip) return;
    var bs = this.bundleState();
    bar.classList.toggle('has-offer', !!bs);
    [line, strip].forEach(function (el) { if (el) el.hidden = !bs; });
    if (!bs) return;
    var L = bs.b.labels || {}, m = this.bundleMap(v, bs);
    var tpl = bs.state === 'unlocked' ? L.fUnlocked : bs.state === 'progress' ? L.fProgress : L.fEmpty;
    var txt = esc(fill(tpl, m));
    // Progress: the reward after the arrow reads medium ("Add 1 more → 40% off").
    if (bs.state === 'progress' && txt.indexOf('→') > -1) { var i = txt.indexOf('→') + 1; txt = txt.slice(0, i) + ' <b>' + txt.slice(i).replace(/^\s+/, '') + '</b>'; }
    [line, strip].forEach(function (el) {
      if (!el) return;
      el.className = (el === line ? 'a2-fatc__offer' : 'a2-fatc__strip ys-only-m') + ' is-' + bs.state;
      el.innerHTML = el === strip ? '<span>' + txt + '</span>' : txt;
      if (el === strip) el.style.setProperty('--a2-bp', (bs.state === 'progress' ? bs.fill : 0) + '%');
    });
  };
  Main.prototype.refreshBundle = function (delay) {
    var self = this, b = this.config && this.config.bundle;
    if (!b || !b.on || !b.countUrl) return;
    setTimeout(function () {
      var token = self.bundleToken = (self.bundleToken || 0) + 1;
      fetch(b.countUrl, { credentials: 'same-origin', cache: 'no-store' }).then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); }).then(function (html) {
        var mm = /data-a2-bundle-count="(\d+)"/.exec(html);
        if (!mm || token !== self.bundleToken) return;
        var n = parseInt(mm[1], 10);
        if (n === self.bundleCount) return;
        var before = self.bundleState();
        self.bundleCount = n;
        var after = self.bundleState();
        if (before && after && before.state !== after.state) {
          track('pdp_bundle_state', { from: before.state, to: after.state, count: n });
          var live = $('[data-a2-bundle-live]', self.root);
          if (live) live.textContent = self.bundleSentence(self.findVariant(self.state.colour, self.state.stretch), after);
        }
        self.updateVariantUI(false);
      }).catch(function () { /* keep the last state */ });
    }, delay || 0);
  };

  Main.prototype.updateFloating = function (v) {
    var l1 = this.root && $('[data-a2-fatc-l1]', this.root);
    if (!l1) return;
    l1.textContent = [this.state.colour, this.state.stretch].filter(Boolean).join(' · ');
    var idx = this.stretchValues.indexOf(this.state.stretch);
    $('[data-a2-fatc-l2]', this.root).textContent = this.config.accessory ? '' : (this.config.stretchRanges || [])[idx] || '';
    if (!this.adding) this.updateBundleFloating(v);
    var btn = $('[data-a2-fatc-btn]', this.root);
    if (!this.adding) btn.textContent = v && v.available ? this.labels.atc + ' — ' + v.price : this.labels.soldOut;
    btn.disabled = !(v && v.available);
  };

  /* ---------- Style switcher (§1) ---------- */
  Main.prototype.toggleSwitcher = function (part, trigger) {
    var dlg = $('[data-a2-sw]', this.root);
    if (!dlg) return;
    if (dlg.open && this.swPart === part) { this.closeSwitcher(); return; }
    this.swPart = part; this.swTrigger = trigger;
    this.renderTiles(part);
    $$('[data-a2-switch-open]', this.root).forEach(function (b) { b.setAttribute('aria-expanded', b === trigger ? 'true' : 'false'); });
    if (!dlg.open) {
      if (mqDesktop.matches) dlg.show(); else { dlg.showModal(); lockScroll(true); this.bindSheetSwipe(dlg); }
    }
    this.root.classList.toggle('is-switch-open', mqDesktop.matches);
    var title = $('[data-a2-sw-title]', dlg); if (title) title.focus({ preventScroll: true });
    if (!mqDesktop.matches) this.prefetchAll();
    this.syncFloating();
    track('pdp_style_open', { part: part });
  };
  Main.prototype.closeSwitcher = function () {
    var dlg = $('[data-a2-sw]', this.root);
    if (!dlg || !dlg.open) return;
    dlg.close(); lockScroll(false);
    this.root.classList.remove('is-switch-open');
    $$('[data-a2-switch-open]', this.root).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (this.swTrigger && document.contains(this.swTrigger)) this.swTrigger.focus({ preventScroll: true });
    this.syncFloating();
  };
  Main.prototype.bindSheetSwipe = function (dlg) {
    if (dlg._a2Swipe) return; dlg._a2Swipe = true;
    var self = this, y0 = null;
    var head = $('.a2-sw__grab', dlg), head2 = $('.a2-sw__head', dlg);
    [head, head2].forEach(function (h) {
      if (!h) return;
      h.addEventListener('touchstart', function (e) { y0 = e.touches[0].clientY; }, { passive: true });
      h.addEventListener('touchend', function (e) { if (y0 != null && e.changedTouches[0].clientY - y0 > 80) self.closeSwitcher(); y0 = null; }, { passive: true });
    });
    var body = $('[data-a2-sw-body]', dlg);
    if (body) body.addEventListener('scroll', function () { self.updateScrollHint(); }, { passive: true });
  };
  Main.prototype.tileList = function (part) {
    var cfg = this.config, names = part === 'top' ? cfg.tops : cfg.bottoms, tiles = (cfg.tiles || {})[part] || {};
    var lookup = {}; Object.keys(tiles).forEach(function (k) { lookup[k.trim().toLowerCase()] = tiles[k]; });
    // "Top only" / "Bottom only" are real options (they're in the product map, as in mix-match): listed last.
    return (names || []).map(function (n, i) {
      var t = lookup[n.trim().toLowerCase()] || {}, solo = isSolo(n);
      // Tile shows the style name (as the design does). Descriptor = the new switcher_descriptor
      // field; until that's filled, the quiz's own display label ("Classic", "Racer") when it
      // differs from the name.
      var interim = t.label && t.label.toLowerCase() !== n.toLowerCase() ? t.label : '';
      return { name: n, label: n, solo: solo, descriptor: t.descriptor || interim, diagram: t.diagram, diagramOnePiece: t.diagramOnePiece, order: solo ? 5000 + i : t.order != null && t.order !== 999 ? t.order : 1000 + i };
    }).sort(function (a, b) { return a.order - b.order; });
  };
  Main.prototype.renderTiles = function (part) {
    var cfg = this.config, L = this.labels, dlg = $('[data-a2-sw]', this.root), grid = $('[data-a2-sw-grid]', dlg);
    var curTop = cfg.currentTop, curBottom = cfg.currentBottom, other = part === 'top' ? curBottom : curTop;
    var onePiece = isOnePiece(curBottom);
    var anyDisabled = false, cross = cfg.crossSwitch !== false, self = this;
    var clean = function (x) { return String(x || '').replace(/^[\s·]+/, '').trim(); };
    var tile = function (t) {
      var key = part === 'top' ? t.name + '__' + curBottom : curTop + '__' + t.name;
      var handle = cfg.productMap[key], partner = '';
      var current = part === 'top' ? t.name === curTop : t.name === curBottom;
      // Not made with the current other part: with cross-switching on, jump to the best
      // combination that does exist ("with Dress"); otherwise grey it out as before.
      var partners = [];
      if (!handle && !current && cross) { partners = self.partnersFor(part, t.name); partner = partners[0] || ''; handle = partner ? cfg.productMap[part === 'top' ? t.name + '__' + partner : partner + '__' + t.name] : ''; }
      var disabled = !handle && !current;
      if (disabled) anyDisabled = true;
      // Shopify trims text settings, so join the descriptor and the suffix here
      // ("Racer · not with High Waist"; just "not with High Waist" with no descriptor).
      // Tops whose first-named combination is a one-piece / dress / romper read "As a Dress",
      // "As a One-Piece and 2 others"; mixed pairs read "As a One-Piece or with High Waist".
      var asA = part === 'top' && partners.length && isOnePiece(partners[0]), n = partners.length;
      var tpl = n > 2 ? (asA ? L.partnersManyAs : L.partnersMany)
        : n === 2 ? (asA ? (isOnePiece(partners[1]) ? L.partnersTwoAs : L.partnersTwoAsMixed) : L.partnersTwo)
        : (asA ? L.partnersOneAs : L.partnersOne);
      var suffix = partner ? fill(tpl, { first: partners[0], second: partners[1] || '', n: partners.length - 1 }) : disabled ? ((part === 'top' && onePiece) ? L.twoPieceOnly : fill(L.notWith, { other: other })) : '';
      var desc = [clean(t.descriptor), clean(suffix)].filter(Boolean).join(' · ');
      var img = part === 'top' && onePiece && t.diagramOnePiece ? t.diagramOnePiece : t.diagram;
      var well = img ? '<img src="' + img + '" alt="" decoding="async">'
        : esc(t.label.slice(0, 2));
      return { cross: !!partner, disabled: disabled, html: '<button type="button" class="a2-tile' + (t.solo ? ' a2-tile--solo' : '') + '" role="radio" data-a2-tile="' + esc(t.name) + '" data-a2-handle="' + esc(handle || '') + '"' +
        (partner ? ' data-a2-partner="' + esc(partner) + '"' : '') +
        (current ? ' aria-current="true" aria-checked="true"' : ' aria-checked="false"') + (disabled ? ' disabled' : '') + ' tabindex="' + (current ? '0' : '-1') + '">' +
        (t.solo ? '' : '<span class="a2-tile__well">' + well + '</span>') + '<span><span class="a2-tile__name">' + esc(t.label) + '</span>' +
        (desc ? '<span class="a2-tile__desc">' + esc(desc) + '</span>' : '') + '</span>' +
        (current ? '<span class="a2-tile__tick"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>' : '') +
        '</button>' };
    };
    var tiles = this.tileList(part).map(tile);
    // With cross-switching on, a style that's still greyed has no product with ANY partner: leave it out.
    if (cross) { tiles = tiles.filter(function (t) { return !t.disabled; }); anyDisabled = false; }
    var direct = tiles.filter(function (t) { return !t.cross; }), crossed = tiles.filter(function (t) { return t.cross; });
    var head = function (txt) { return txt ? '<p class="a2-sw__group" role="presentation">' + esc(txt) + '</p>' : ''; };
    var html = crossed.length
      ? head(fill(isSolo(other) ? L.groupSolo : part === 'top' ? (onePiece ? L.groupWithOnePiece : L.groupWithTop) : L.groupWithBottom, { other: other })) + direct.map(function (t) { return t.html; }).join('') +
        head(part === 'top' ? L.groupOtherTop : L.groupOtherBottom) + crossed.map(function (t) { return t.html; }).join('')
      : tiles.map(function (t) { return t.html; }).join('');
    grid.innerHTML = html;
    var title = part === 'top' ? L.chooseTop : L.chooseBottom;
    $('[data-a2-sw-title]', dlg).textContent = title;
    dlg.setAttribute('aria-label', title);
    var sub = mqDesktop.matches ? (part === 'top' ? L.subTop : L.subBottom) : (part === 'top' ? L.subMobileTop : L.subMobileBottom);
    // The group headings already say what goes with what, so the subtitle is dropped when grouping.
    var subEl = $('[data-a2-sw-sub]', dlg);
    subEl.textContent = crossed.length ? '' : fill(sub, { other: other });
    subEl.hidden = !!crossed.length;
    var note = $('[data-a2-sw-note]', dlg);
    note.textContent = anyDisabled ? (onePiece && part === 'top' ? L.noteOnePiece : fill(L.noteOther, { other: other })) : '';
    note.dataset.mode = anyDisabled ? 'note' : '';
    var sep = $('[data-a2-sw-sep]', dlg), url = part === 'top' ? (cfg.separates || {}).bottoms : (cfg.separates || {}).tops;
    // The "shop bottoms/tops only" link is redundant when "Bottom only" / "Top only" is a tile.
    if (((part === 'top' ? cfg.tops : cfg.bottoms) || []).some(isSolo)) url = '';
    sep.hidden = !url;
    if (url) { sep.href = url; sep.textContent = mqDesktop.matches ? (part === 'top' ? L.separatesTop : L.separatesBottom) : (part === 'top' ? L.separatesTopMobile : L.separatesBottomMobile); }
    this.updateScrollHint();
  };
  Main.prototype.updateScrollHint = function () {
    var dlg = $('[data-a2-sw]', this.root), body = dlg && $('[data-a2-sw-body]', dlg), note = dlg && $('[data-a2-sw-note]', dlg);
    if (!body || !note || mqDesktop.matches || note.dataset.mode === 'note') return;
    note.textContent = body.scrollHeight - body.scrollTop - body.clientHeight > 8 ? this.labels.scrollHint : '';
  };
  Main.prototype.moveTileFocus = function (tile, key, e) {
    var tiles = $$('[data-a2-tile]:not([disabled])', this.root), i = tiles.indexOf(tile), cols = mqDesktop.matches ? 3 : 2;
    var d = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols }[key];
    var n = tiles[Math.max(0, Math.min(tiles.length - 1, i + d))];
    if (n) { e.preventDefault(); tiles.forEach(function (t) { t.tabIndex = -1; }); n.tabIndex = 0; n.focus(); }
  };
  // Every existing partner for a style that isn't made with the current other part, best first:
  // same one-piece/two-piece kind as now, then the merchant's list order. "… only" is dropped
  // when there's a real partner. The first one is where a tap goes.
  Main.prototype.partnersFor = function (part, name) {
    var cfg = this.config, isTop = part === 'top', order = (isTop ? cfg.bottoms : cfg.tops) || [];
    var wantOne = isOnePiece(cfg.currentBottom);
    var rank = function (n) {
      var i = order.indexOf(n);
      return (isTop && isOnePiece(n) !== wantOne ? 1000 : 0) + (i < 0 ? 999 : i);
    };
    var all = Object.keys(cfg.productMap || {}).map(function (k) { return k.split('__'); })
      .filter(function (p) { return p.length === 2 && p[isTop ? 0 : 1] === name; })
      .map(function (p) { return p[isTop ? 1 : 0]; });
    var real = all.filter(function (n) { return !isSolo(n); });
    return (real.length ? real : all).sort(function (a, b) { return rank(a) - rank(b); });
  };
  Main.prototype.chooseTile = function (tileEl) {
    var part = this.swPart, cfg = this.config, name = tileEl.dataset.a2Tile, partner = tileEl.dataset.a2Partner;
    var top = part === 'top' ? name : (partner || cfg.currentTop), bottom = part === 'bottom' ? name : (partner || cfg.currentBottom);
    var handle = cfg.productMap[top + '__' + bottom];
    this.closeSwitcher();
    if (!handle || handle === cfg.currentHandle) return;
    var meta = { from_handle: cfg.currentHandle, to_handle: handle, part: part, top: top, bottom: bottom, cross: !!partner };
    this.switchProduct(handle, meta, true);
  };
  Main.prototype.sectionUrl = function (handle) {
    var q = new URLSearchParams({ view: 'a2', section_id: this.sectionId });
    var pv = new URLSearchParams(location.search).get('preview_theme_id');
    if (pv) q.set('preview_theme_id', pv);
    return '/products/' + handle + '?' + q.toString();
  };
  Main.prototype.fetchSection = function (handle) {
    if (!this.fetchCache[handle]) {
      var self = this;
      this.fetchCache[handle] = fetch(this.sectionUrl(handle), { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) {
        if (html.indexOf('data-a2-main') === -1) throw new Error('No a2 section in response');
        return html;
      }).catch(function (err) { delete self.fetchCache[handle]; throw err; });
    }
    return this.fetchCache[handle];
  };
  Main.prototype.prefetch = function (handle) { if (handle) this.fetchSection(handle).catch(function () {}); };
  Main.prototype.prefetchAll = function () {
    var self = this;
    $$('[data-a2-tile]:not([disabled])', this.root).slice(0, 6).forEach(function (t) { self.prefetch(t.dataset.a2Handle); });
  };
  Main.prototype.switchProduct = function (handle, meta, push) {
    var self = this, keep = { colour: this.state.colour, stretch: this.state.stretch };
    var err = $('[data-a2-switch-error]', this.root); if (err) err.hidden = true;
    this.root.classList.add('is-loading');
    var bar = $('[data-a2-progress]', this.root); if (bar) bar.hidden = false;
    return Promise.all([this.fetchSection(handle), new Promise(function (r) { setTimeout(r, 150); })]).then(function (res) {
      var doc = new DOMParser().parseFromString(res[0], 'text/html');
      var fresh = doc.querySelector('[data-a2-main]');
      self.root.replaceWith(document.importNode(fresh, true));
      self.hydrate();
      paintSwatches();
      // Restore colour + Stretch where they exist on the new product (§1.5 step 6).
      var colours = $$('[data-a2-colour]', self.root).map(function (i) { return i.value; });
      var colour = colours.indexOf(keep.colour) > -1 ? keep.colour : self.firstAvailableColour();
      var stretch = self.stretchValues.indexOf(keep.stretch) > -1 ? keep.stretch : self.stretchValues[0];
      var v = self.findVariant(colour, stretch);
      if (!v || !v.available) { var alt = self.findVariant(colour, self.otherStretch(stretch)); if (alt && alt.available && !(v && v.available)) stretch = alt.stretch; }
      self.state = { colour: colour, stretch: stretch };
      self.updateVariantUI(false);
      self.renderGallery(false);
      var cur = self.currentVariant();
      if (push) history.pushState({ a2: true, handle: handle }, '', self.urlFor(handle, cur && cur.id));
      else history.replaceState({ a2: true, handle: handle }, '', self.urlFor(handle, cur && cur.id));
      document.title = (self.config.title || '') + ' – Youswim';
      self.refreshDependents(handle);
      if (window.A2Reviews) window.A2Reviews.render(self.config.productId);
      if (meta) track('pdp_style_switch', meta);
    }).catch(function () {
      self.root.classList.remove('is-loading');
      if (bar) bar.hidden = true;
      if (err) { err.textContent = self.labels.switchError; err.hidden = false; }
    });
  };
  Main.prototype.firstAvailableColour = function () {
    for (var i = 0; i < this.variantList.length; i++) if (this.variantList[i].available) return this.variantList[i].colour;
    return this.variantList[0] && this.variantList[0].colour;
  };
  // Other product-dependent a2 sections (range, related, reviews) refresh from the same page render.
  Main.prototype.refreshDependents = function (handle) {
    var ids = $$('[data-a2-dependent]').map(function (el) { return el.closest('.shopify-section'); }).filter(Boolean).map(function (s) { return s.id.replace('shopify-section-', ''); });
    if (!ids.length) return;
    var q = new URLSearchParams({ view: 'a2', sections: ids.join(',') });
    var pv = new URLSearchParams(location.search).get('preview_theme_id'); if (pv) q.set('preview_theme_id', pv);
    fetch('/products/' + handle + '?' + q.toString()).then(function (r) { return r.json(); }).then(function (json) {
      ids.forEach(function (id) {
        if (!json[id]) return;
        var doc = new DOMParser().parseFromString(json[id], 'text/html');
        var fresh = doc.getElementById('shopify-section-' + id), cur = document.getElementById('shopify-section-' + id);
        if (fresh && cur) { cur.innerHTML = fresh.innerHTML; document.dispatchEvent(new CustomEvent('a2:section-refreshed', { detail: { id: id } })); }
      });
    }).catch(function () {});
  };

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  /* ---------------------------------------------------------------------
     How-it-works modal (§10). Rendered by sections/a2-how-it-works.liquid.
     --------------------------------------------------------------------- */
  var Hiw = {
    init: function () {
      document.addEventListener('click', function (e) {
        var t = e.target.closest && e.target.closest('[data-a2-hiw-open]');
        if (t) { e.preventDefault(); Hiw.open(t); return; }
        var dlg = document.querySelector('[data-a2-hiw]');
        if (!dlg || !dlg.open) return;
        if (e.target === dlg || e.target.closest('[data-a2-hiw-close]')) { Hiw.close(); return; }
        var chip = e.target.closest('[data-a2-hiw-size]');
        if (chip) { Hiw.pick(chip.dataset.a2HiwSize, true); return; }
        if (e.target.closest('[data-a2-hiw-choose]')) {
          document.dispatchEvent(new CustomEvent('a2:choose-stretch', { detail: { stretch: Hiw.stretch } }));
          track('pdp_hiw_choose', { stretch: Hiw.stretch });
          Hiw.close(true);
          return;
        }
        if (e.target.closest('[data-quiz-open]')) { track('pdp_hiw_quiz'); Hiw.close(true); }
      });
      document.addEventListener('a2:usual-size', function (e) { if (e.detail.source !== 'modal') Hiw.pick(e.detail.size, false); });
      // Mobile sheet: swipe the handle/header down more than 80px to close (as the switcher sheet).
      var y0 = null;
      document.addEventListener('touchstart', function (e) { var h = e.target.closest && e.target.closest('.a2-hiw .a2-hiw__top'); y0 = h ? e.touches[0].clientY : null; }, { passive: true });
      document.addEventListener('touchend', function (e) { if (y0 != null && e.changedTouches[0].clientY - y0 > 80) Hiw.close(); y0 = null; }, { passive: true });
    },
    dlg: function () { return document.querySelector('[data-a2-hiw]'); },
    // One size map for the page: the buy box's "I usually wear" setting.
    mainMap: function () {
      var main = document.querySelector('[data-a2-main]');
      return parseUsualMap(main ? (readJSON(main, 'data-a2-config') || {}).usualSizeMap : '');
    },
    open: function (trigger) {
      var dlg = Hiw.dlg(); if (!dlg) return;
      Hiw.trigger = trigger;
      Hiw.cfg = readJSON(dlg, 'data-a2-hiw-config') || {};
      Hiw.map = Hiw.mainMap();
      Hiw.pick(Usual.get() || String(Hiw.cfg.defaultSize || 10), false);
      dlg.showModal(); lockScroll(true);
      var v = dlg.querySelector('video');
      if (v) { loadVideo(v); if (!reduceMotion.matches) v.play().catch(function () {}); }
      var title = dlg.querySelector('[data-a2-hiw-title]'); if (title) title.focus({ preventScroll: true });
      if (trigger.dataset.a2HiwTarget === 'find') {
        var find = dlg.querySelector('[data-a2-hiw-find]');
        if (find) setTimeout(function () { find.scrollIntoView({ block: 'start' }); }, 50);
      } else { var sc = dlg.querySelector('.a2-hiw__scroll'); if (sc) sc.scrollTop = 0; }
      track('pdp_hiw_open', { trigger: trigger.dataset.a2HiwOpen || 'link' });
      dlg.addEventListener('close', Hiw.onClose, { once: true });
    },
    onClose: function () {
      lockScroll(false);
      var v = Hiw.dlg().querySelector('video'); if (v) v.pause();
      if (!Hiw.keepFocus && Hiw.trigger && document.contains(Hiw.trigger)) Hiw.trigger.focus({ preventScroll: true });
      Hiw.keepFocus = false;
    },
    close: function (keepFocus) { var d = Hiw.dlg(); Hiw.keepFocus = !!keepFocus; if (d && d.open) d.close(); },
    pick: function (size, user) {
      var dlg = Hiw.dlg(); if (!dlg) return;
      Hiw.cfg = Hiw.cfg || readJSON(dlg, 'data-a2-hiw-config') || {};
      Hiw.map = Hiw.map || Hiw.mainMap();
      var row = null; Hiw.map.forEach(function (r) { if (r.size === String(size)) row = r; });
      if (!row) return;
      Hiw.stretch = row.stretch;
      $$('[data-a2-hiw-size]', dlg).forEach(function (c) { c.setAttribute('aria-pressed', c.dataset.a2HiwSize === row.size ? 'true' : 'false'); });
      $$('[data-a2-hiw-card]', dlg).forEach(function (c) { c.classList.toggle('is-active', c.dataset.a2HiwCard === row.stretch); });
      $$('[data-a2-hiw-result]', dlg).forEach(function (el) {
        el.innerHTML = esc(fill(el.dataset.tpl, { size: sizeNum(row.size), stretch: '\u0000' })).replace('\u0000', '<strong>' + esc(row.stretch) + '</strong>');
      });
      var b = dlg.querySelector('[data-a2-hiw-boundary]');
      if (b) b.hidden = !(row.size === '12' || row.size === '14');
      var choose = dlg.querySelector('[data-a2-hiw-choose]');
      if (choose) choose.textContent = fill(choose.dataset.tpl, { stretch: row.stretch });
      if (user) { Usual.set(row.size, 'modal'); track('pdp_hiw_size_pick', { usual_size: row.size }); }
    }
  };

  /* ---------------------------------------------------------------------
     Small shared behaviours for the content sections.
     --------------------------------------------------------------------- */
  function initContent() {
    // Accordions: animate height (skip with reduced motion). FAQ analytics.
    document.addEventListener('click', function (e) {
      var sum = e.target.closest && e.target.closest('.ys details > summary');
      if (!sum || reduceMotion.matches) return;
      var det = sum.parentElement, panel = det.querySelector(':scope > :not(summary)');
      if (!panel || !panel.animate) return;
      e.preventDefault();
      if (det.open) {
        var h = panel.offsetHeight;
        panel.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 200, easing: 'cubic-bezier(.2,.7,.2,1)' }).onfinish = function () { det.open = false; };
      } else {
        det.open = true;
        var h2 = panel.offsetHeight;
        panel.animate([{ height: '0px', opacity: 0 }, { height: h2 + 'px', opacity: 1 }], { duration: 200, easing: 'cubic-bezier(.2,.7,.2,1)' });
        if (det.matches('[data-a2-faq-item]')) track('pdp_faq_open', { question_index: det.dataset.index });
      }
    });
    // Range tabs.
    document.addEventListener('click', function (e) {
      var tab = e.target.closest && e.target.closest('[data-a2-range-tab]');
      if (!tab) return;
      var root = tab.closest('[data-a2-range]');
      $$('[data-a2-range-tab]', root).forEach(function (t) { t.setAttribute('aria-selected', t === tab ? 'true' : 'false'); });
      $$('[data-a2-range-panel]', root).forEach(function (p) { p.hidden = p.dataset.a2RangePanel !== tab.dataset.a2RangeTab; });
    });
    // Compare CTA → focus the usual-size select.
    document.addEventListener('click', function (e) {
      var cta = e.target.closest && e.target.closest('[data-a2-compare-cta]');
      if (cta) {
        e.preventDefault(); track('pdp_compare_cta');
        var target = document.querySelector('[data-a2-usual]') || document.querySelector('[data-a2-size]');
        if (target) { target.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' }); setTimeout(function () { target.focus({ preventScroll: true }); }, 450); }
      }
      var sd = e.target.closest && e.target.closest('[data-a2-still-cta]');
      if (sd) track('pdp_still_deciding', { cta: sd.dataset.a2StillCta });
    });
    initPress();
    loadRelated();
    document.addEventListener('a2:section-refreshed', function () { initPress(); loadRelated(); initLazyVideos(); });
  }
  // Wear it with: Shopify recommendations re-render the section with `recommendations`
  // populated. Complementary first, then related if the merchant hasn't set any.
  function loadRelated() {
    $$('[data-a2-related][data-url]').forEach(function (root) {
      if (root._a2) return; root._a2 = true;
      var load = function (url, fallback) {
        fetch(url).then(function (r) { return r.text(); }).then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-a2-related]');
          if (fresh && fresh.querySelector('.a2-card')) root.innerHTML = fresh.innerHTML;
          else if (fallback) load(fallback);
        }).catch(function () {});
      };
      var u = root.dataset.url;
      load(u, u.indexOf('intent=complementary') > -1 ? u.replace('intent=complementary', 'intent=related') : null);
    });
  }
  // Press quotes. Desktop: a window of N quotes (setting "Quotes shown on desktop") rotates
  // through all of them. Mobile: one at a time. Logos (and mobile dots) jump to their quote.
  // Autoplay pauses on hover/focus, while offscreen and with reduced motion.
  function initPress() {
    $$('[data-a2-press]').forEach(function (root) {
      if (root._a2) return; root._a2 = true;
      // Logo row order: as set, reversed (server-side) or shuffled per page view. Logos keep their
      // data-a2-press-logo index, so each still selects its own quote.
      var logoRow = $('.a2-press__logos', root);
      if (logoRow && root.dataset.logoOrder === 'random') {
        var ls = Array.prototype.slice.call(logoRow.children);
        for (var q = ls.length - 1; q > 0; q--) { var r = Math.floor(Math.random() * (q + 1)), tmp = ls[q]; ls[q] = ls[r]; ls[r] = tmp; }
        ls.forEach(function (el) { logoRow.appendChild(el); });
      }
      var figs = $$('[data-a2-press-fig]', root), n = figs.length;
      if (n < 2) return;
      var dots = $$('[data-a2-press-dot]', root), logos = $$('[data-a2-press-logo]', root);
      var per = Math.max(1, Math.min(n, parseInt(root.dataset.perDesktop, 10) || 3));
      var i = 0, timer = null, visible = false, held = false;
      function render() {
        var d = mqDesktop.matches;
        figs.forEach(function (f, k) {
          var pos = (k - i + n) % n, shown = d ? pos < per : k === i;
          f.style.order = d ? pos : '';
          f.classList.toggle('is-shown', d && shown);
          f.classList.toggle('is-active', k === i);
        });
        dots.forEach(function (el, k) { el.classList.toggle('is-active', k === i); el.setAttribute('aria-current', k === i ? 'true' : 'false'); });
        logos.forEach(function (el) {
          var k = +el.dataset.a2PressLogo, pos = (k - i + n) % n, on = d ? pos < per : k === i;
          var img = el.querySelector('.a2-press__logo'); if (img) img.classList.toggle('is-quoted', on);
          el.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      var quotes = $('.a2-press__quotes', root);
      function reserve() {
        // Desktop: hold the height of the tallest quote so rotating sets never move the page.
        quotes.style.minHeight = '';
        if (!mqDesktop.matches) return;
        var max = 0;
        figs.forEach(function (f) { var was = f.classList.contains('is-shown'); f.classList.add('is-shown'); max = Math.max(max, f.offsetHeight); if (!was) f.classList.remove('is-shown'); });
        quotes.style.minHeight = max + 'px';
      }
      function go(k) { i = (k + n) % n; render(); }
      function step() { go(i + (mqDesktop.matches ? per : 1)); }
      function play() {
        clearInterval(timer);
        var secs = parseInt(mqDesktop.matches ? root.dataset.autoplayDesktop : root.dataset.autoplay, 10) || 0;
        if (secs && visible && !held && !reduceMotion.matches) timer = setInterval(step, secs * 1000);
      }
      root.addEventListener('click', function (e) {
        var t = e.target.closest('[data-a2-press-logo], [data-a2-press-dot]');
        if (!t || t.tagName === 'A') return;
        var k = t.hasAttribute('data-a2-press-logo') ? +t.dataset.a2PressLogo : +t.dataset.a2PressDot;
        // Desktop: bring the clicked quote to the front of the window (leftmost).
        go(k); play();
      });
      root.addEventListener('mouseenter', function () { held = true; play(); });
      root.addEventListener('mouseleave', function () { held = false; play(); });
      root.addEventListener('focusin', function () { held = true; play(); });
      root.addEventListener('focusout', function () { held = false; play(); });
      new IntersectionObserver(function (es) { visible = es[0].isIntersecting; play(); }).observe(root);
      bindSwipe(quotes, function (dir) { go(i + dir * (mqDesktop.matches ? per : 1)); play(); });
      mqDesktop.addEventListener('change', function () { reserve(); render(); play(); });
      window.addEventListener('resize', function () { clearTimeout(root._a2r); root._a2r = setTimeout(function () { reserve(); render(); }, 150); });
      reserve(); render();
    });
  }

  // Swatch colours: the theme stores them in assets/swatches.json (theme.assets.swatches),
  // matched by lower-cased, space-stripped name — the same rule theme.dev.js ColorMatch uses.
  var swatchData = null;
  /* ---------- Lazy, right-sized videos (snippets/a2-video.liquid) ---------- */
  // src is set only when a video comes into view (≤720p on phones, ≤1080p on desktop);
  // autoplay videos play while visible and pause when scrolled/swiped away. Clipping by the
  // gallery's horizontal scroller counts, so the loop video waits until it's swiped to.
  function loadVideo(v) {
    if (!v || v.getAttribute('src')) return;
    var src = mqDesktop.matches ? (v.dataset.srcHd || v.dataset.srcSd) : (v.dataset.srcSd || v.dataset.srcHd);
    if (src) v.src = src;
  }
  var videoIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var v = e.target;
      if (e.isIntersecting) {
        if (!v.hasAttribute('data-a2-autoplay')) return;
        loadVideo(v);
        if (!reduceMotion.matches) v.play().catch(function () {});
      } else if (!v.paused) v.pause();
    });
  }, { rootMargin: '100px 0px' }) : null;
  function initLazyVideos(scope) {
    $$('[data-a2-lazy-video]', scope || document).forEach(function (v) {
      if (v._a2io) return; v._a2io = true;
      if (videoIO) videoIO.observe(v); else if (v.hasAttribute('data-a2-autoplay')) { loadVideo(v); v.play().catch(function () {}); }
    });
  }

  function paintSwatches() {
    var labels = $$('[data-a2-swatch-name]');
    if (!labels.length || !window.theme || !theme.assets || !theme.assets.swatches) return;
    swatchData = swatchData || fetch(theme.assets.swatches).then(function (r) { return r.json(); }).then(function (d) {
      var map = {};
      (d.colors || []).forEach(function (o) { var k = Object.keys(o)[0]; map[k.toLowerCase().replace(/\s/g, '')] = o[k]; });
      return map;
    }).catch(function () { return {}; });
    swatchData.then(function (map) {
      var base = (theme.assets.base || '/');
      $$('[data-a2-swatch-name]').forEach(function (l) {
        var v = map[l.dataset.a2SwatchName.toLowerCase().replace(/\s/g, '')];
        if (!v) return;
        l.style.setProperty('--a2-swatch', /\.(jpe?g|png|svg|gif)$/i.test(v) ? 'url(' + base + v + ')' : v);
      });
    });
  }

  function boot() {
    paintSwatches();
    initLazyVideos();
    $$('[data-a2-main]').forEach(function (el) {
      var wrapper = el.closest('.shopify-section') || el.parentElement;
      if (!wrapper._a2) wrapper._a2 = new Main(wrapper);
    });
    var first = document.querySelector('[data-a2-main]');
    if (first) {
      var cfg = readJSON(first, 'data-a2-config') || {};
      history.replaceState({ a2: true, handle: cfg.currentHandle }, '', location.href);
      track('pdp_view', { handle: cfg.currentHandle, top: cfg.currentTop, bottom: cfg.currentBottom });
    }
  }

  window.A2Pdp = { Main: Main, Hiw: Hiw, Usual: Usual };
  Hiw.init();
  initContent();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
